/**
 * The website's Market page (website roadmap S11 — weav3r's price finder and categories): one item's Item Market
 * listings, its bazaar listings, Torn's values and what to list it for. Pure: whoever asks passes the answers in.
 *
 * - Item Market: Torn v2 `/market/{id}/itemmarket?limit=100` (public data), read by the Omni-Torn server on demand
 *   (lent keys first, else the server's own key — apps/api/src/item-market.ts), kept MARKET_KEEP_MS.
 * - Bazaars: weav3r.dev's public list for the item, ONLY through the player's own extension (its weav3r connection,
 *   bg/bazaars.ts; the site asks with a `bazaar` site-bridge message). It stays on that page: never sent to or asked by
 *   the Omni-Torn server, never into the player database.
 */

import type { Rarity } from './loadout-intel';
import { cleanPoolListing, type GearBonus, type GearStats } from './market-pool';

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x) ? Number(x) : null);
const str = (x: unknown): string | null => (typeof x === 'string' && x.trim() ? x.trim() : null);
const posInt = (x: unknown): number | null => {
  const n = num(x);
  return n !== null && Number.isInteger(n) && n > 0 ? n : null;
};

/** How long the server keeps an item's Item Market listings (the roadmap's 2–5 minutes). */
export const MARKET_KEEP_MS = 3 * 60_000;
/** Item Market reads the server makes a minute for the website, in all. */
export const MARKET_PER_MINUTE = 10;
/** …and for one player asking. */
export const MARKET_PER_ASKER = 5;
/** Listings asked of Torn (its most for one call). */
export const MARKET_LISTINGS = 100;
/** How long the website keeps an item's bazaar listings on the page before asking the extension again. */
export const BAZAAR_KEEP_MS = 10 * 60_000;
/** Bazaar lists the extension fetches from weav3r.dev for the website, a minute (weav3r allows 200 a minute per address). */
export const BAZAAR_SITE_PER_MINUTE = 30;

export interface MarketListing {
  price: number;
  amount: number;
  /** A weapon's or armor's copy (Torn's `item_details`: uid, stats, bonuses, rarity) — the Gear page (S31) uses it. */
  details?: { uid: number | null; stats: GearStats | null; bonuses: GearBonus[]; rarity: Rarity | null };
}

export interface ItemMarketRead {
  itemId: number;
  name: string | null;
  /** Torn's average price for the item (`average_price`), when it sends one. */
  average: number | null;
  /** Cheapest first, at most MARKET_LISTINGS. */
  listings: MarketListing[];
  /** When Torn made its own copy (ms; `cache_timestamp`), when it says. */
  tornAt: number | null;
}

/** The server's answer (GET /api/market/item/:id). */
export interface ItemMarketAnswer extends ItemMarketRead {
  /** When the server read it (ms). */
  at: number;
  /** Torn calls this answer cost (0: the server's kept copy). */
  calls: number;
}

/** Torn v2 `/market/{id}/itemmarket`: `{ itemmarket: { item: { id, name, average_price }, listings: [{ price, amount }], cache_timestamp } }`. */
export function readItemMarket(body: unknown, itemId?: number): ItemMarketRead | null {
  const m = obj(obj(body)?.itemmarket);
  if (!m || !Array.isArray(m.listings)) return null;
  const item = obj(m.item);
  const id = posInt(item?.id) ?? itemId ?? null;
  if (!id) return null;
  const listings = (m.listings as unknown[])
    .map((x): MarketListing | null => {
      const l = obj(x);
      const price = num(l?.price);
      const amount = posInt(l?.amount ?? l?.quantity);
      if (price === null || price <= 0 || !amount) return null;
      const d = obj(l?.item_details ?? l?.itemDetails);
      if (!d) return { price, amount };
      const g = cleanPoolListing({ source: 'itemmarket', itemId: 1, sellerId: 1, price, qty: amount, uid: d.uid, stats: d.stats, bonuses: d.bonuses, rarity: d.rarity }, 0);
      return { price, amount, ...(g ? { details: { uid: g.uid, stats: g.stats, bonuses: g.bonuses, rarity: g.rarity } } : {}) };
    })
    .filter((l): l is MarketListing => !!l)
    .sort((a, b) => a.price - b.price)
    .slice(0, MARKET_LISTINGS);
  const avg = num(item?.average_price);
  const cached = num(m.cache_timestamp);
  return { itemId: id, name: str(item?.name), average: avg && avg > 0 ? avg : null, listings, tornAt: cached && cached > 0 ? cached * 1000 : null };
}

/** A bazaar listing as the extension hands it over (its weav3r reader, core/bazaar.ts `Listing`). */
export interface BazaarListing {
  playerId: number;
  playerName: string | null;
  qty: number;
  price: number;
  /** Unix seconds weav3r last saw it. */
  updated: number | null;
}

export interface BazaarReply {
  itemId: number;
  listings: BazaarListing[];
  /** Torn's market value as weav3r reports it. */
  marketPrice: number | null;
  problem: string | null;
}

/**
 * The extension's answer to the site's `bazaar` message — checked, since it crosses from another script: `{ itemId,
 * listings, marketPrice, problem }` or `{ problem }`. Null when nothing usable came (no extension, or it didn't answer).
 */
export function readBazaarReply(x: unknown, itemId: number): BazaarReply | null {
  const r = obj(x);
  if (!r) return null;
  const problem = str(r.problem);
  if (!Array.isArray(r.listings)) return problem ? { itemId, listings: [], marketPrice: null, problem } : null;
  if (posInt(r.itemId) !== null && posInt(r.itemId) !== itemId) return null;
  const listings = (r.listings as unknown[])
    .map((y): BazaarListing | null => {
      const l = obj(y);
      const playerId = posInt(l?.playerId);
      const price = num(l?.price);
      const qty = posInt(l?.qty);
      if (!playerId || price === null || price <= 0 || !qty) return null;
      return { playerId, playerName: str(l!.playerName), qty, price, updated: num(l!.updated) };
    })
    .filter((l): l is BazaarListing => !!l)
    .sort((a, b) => a.price - b.price || b.qty - a.qty);
  const mp = num(r.marketPrice);
  return { itemId, listings, marketPrice: mp && mp > 0 ? mp : null, problem };
}

/** The middle price of the units listed (each listing counts as many times as its amount). */
export function medianPrice(listings: ReadonlyArray<{ price: number; amount: number }>): number | null {
  const sorted = listings.filter((l) => l.price > 0 && l.amount > 0).sort((a, b) => a.price - b.price);
  const units = sorted.reduce((s, l) => s + l.amount, 0);
  if (!units) return null;
  const at = (k: number): number => {
    let seen = 0;
    for (const l of sorted) {
      seen += l.amount;
      if (k < seen) return l.price;
    }
    return sorted[sorted.length - 1]!.price;
  };
  return units % 2 ? at((units - 1) / 2) : (at(units / 2 - 1) + at(units / 2)) / 2;
}

export interface PriceSummary {
  /** Cheapest on the Item Market. */
  market: number | null;
  /** Cheapest in a bazaar (weav3r, through the extension). */
  bazaar: number | null;
  /** The cheapest anywhere and where. */
  cheapest: { price: number; where: 'market' | 'bazaar' } | null;
  /** Units listed on the Item Market (of the listings read) and their median price. */
  marketUnits: number;
  marketMedian: number | null;
  /** Torn's market value (the items list) and Torn's average price (the Item Market answer). */
  value: number | null;
  average: number | null;
  /** Cheapest anywhere vs the market value, percent (−12 = 12% under). */
  vsValue: number | null;
}

export function priceSummary(market: readonly MarketListing[], bazaar: readonly BazaarListing[], value: number | null, average: number | null): PriceSummary {
  const m = market.length ? Math.min(...market.map((l) => l.price)) : null;
  const b = bazaar.length ? Math.min(...bazaar.map((l) => l.price)) : null;
  const cheapest = m === null && b === null ? null : b !== null && (m === null || b < m) ? { price: b, where: 'bazaar' as const } : { price: m!, where: 'market' as const };
  const v = value && value > 0 ? value : null;
  return {
    market: m,
    bazaar: b,
    cheapest,
    marketUnits: market.reduce((s, l) => s + l.amount, 0),
    marketMedian: medianPrice(market),
    value: v,
    average: average && average > 0 ? average : null,
    vsValue: cheapest && v ? Math.round(((cheapest.price - v) / v) * 1000) / 10 : null,
  };
}

export interface SellSuggestion {
  id: 'undercut' | 'median' | 'percent';
  label: string;
  /** Price to list at (whole dollars, at least $1). */
  price: number;
  /** What you get after the fee. */
  youGet: number;
  note: string;
}

/**
 * What to list it for on the Item Market: $1 under the cheapest listing (sells first), the median listed price (waits
 * longer), or a chosen percent of Torn's market value. `feePct` (0–50) is the player's own selling fee, if any.
 */
export function sellSuggestions(market: readonly MarketListing[], value: number | null, opts: { percent?: number; feePct?: number } = {}): SellSuggestion[] {
  const fee = Math.max(0, Math.min(50, Number(opts.feePct) || 0));
  const pct = Math.max(1, Math.min(1000, Math.round(Number(opts.percent ?? 100)) || 100));
  const out: SellSuggestion[] = [];
  const add = (id: SellSuggestion['id'], label: string, raw: number, note: string) => {
    const price = Math.max(1, Math.round(raw));
    out.push({ id, label, price, youGet: Math.floor(price * (1 - fee / 100)), note });
  };
  const lowest = market.length ? Math.min(...market.map((l) => l.price)) : null;
  if (lowest !== null) add('undercut', 'Lowest − $1', lowest - 1, 'Sells first: just under the cheapest listing.');
  const median = medianPrice(market);
  if (median !== null) add('median', 'Median listing', median, 'Half the units listed are cheaper — sells slower.');
  if (value && value > 0) add('percent', `${pct}% of market value`, (value * pct) / 100, 'From Torn’s market value (its average sale price).');
  return out;
}

export interface ItemCategory {
  /** Torn's item type ("Drug", "Melee"…). */
  type: string;
  count: number;
  /** Items with a market value. */
  valued: number;
}

/** Torn's item types with how many items each has, by name. */
export function itemCategories(items: ReadonlyArray<{ type: string; marketValue: number | null }>): ItemCategory[] {
  const by = new Map<string, ItemCategory>();
  for (const i of items) {
    const c = by.get(i.type) ?? { type: i.type, count: 0, valued: 0 };
    c.count++;
    if (i.marketValue && i.marketValue > 0) c.valued++;
    by.set(i.type, c);
  }
  return [...by.values()].sort((a, b) => a.type.localeCompare(b.type));
}

/**
 * Items whose name matches a search (every word, any order, case ignored; an id matches itself), best first: an exact
 * name, then names starting with the search, then the rest by name.
 */
export function searchItems<T extends { id: number; name: string }>(items: readonly T[], q: string, limit = 20): T[] {
  const s = q.trim().toLowerCase();
  if (!s) return [];
  if (/^\d+$/.test(s)) return items.filter((i) => i.id === Number(s)).slice(0, limit);
  const words = s.split(/\s+/);
  const rank = (n: string) => (n === s ? 0 : n.startsWith(s) ? 1 : 2);
  return items
    .filter((i) => {
      const n = i.name.toLowerCase();
      return words.every((w) => n.includes(w));
    })
    .sort((a, b) => rank(a.name.toLowerCase()) - rank(b.name.toLowerCase()) || a.name.localeCompare(b.name))
    .slice(0, limit);
}
